import React from 'react';
import { Field } from 'react-final-form';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { TabPane } from '../TabPane';
import states from '../../../utils/states';
import {
  useAibList,
  useAffiliates,
  useBrowsers,
  useDevices,
  useDomains,
  useOperatingSystems,
} from '../../../views/OfferBlocks/queries';
import { textarea } from '../../FormFields/reduxFormRenderers';
import { normalizeZips } from '../../../utils/conversionFunctions';
import { ColCondition, MultiselectField, NumberField, SelectRow, ToggleSwitchRow } from '../../FinalForm';
import _ from 'lodash';
import { PreviousRegistration } from './PreviousRegistration';
import { useAffiliateTags } from '../../../views/Affiliates/queries';

const UsersPane = ({ collapse, allDomains, source }) => {
  const generateName = (field) => (source ? `${source}.${field}` : field);

  const { data: domains, isLoading: domainsLoading } = useDomains(true, (data) =>
    data.map((domain) => ({ id: domain.id, name: domain.name })).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: domainTags, isLoading: domainTagsLoading } = useAffiliateTags('domain');
  const domainsWithTags =
    domainsLoading || domainTagsLoading
      ? []
      : [...(domainTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []), ...domains];

  let domainsForPreviousRegReq = domainsWithTags ? [...domainsWithTags] : [];
  domainsForPreviousRegReq.unshift({ name: 'Current Domain', id: null });
  domainsForPreviousRegReq.unshift({ name: 'All Domains', id: null });

  const { data: affiliates, isLoading: affiliatesLoading } = useAffiliates(true, (data) =>
    _.orderBy(data, ['name'], ['asc']).map(({ name, networkAffiliateId }) => ({
      id: networkAffiliateId,
      name,
    }))
  );
  const { data: affiliateTags, isLoading: affiliateTagsLoading } = useAffiliateTags('affiliate');
  const affiliatesWithTags =
    affiliatesLoading || affiliateTagsLoading
      ? []
      : [...(affiliateTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []), ...(affiliates || [])];

  let affiliatesForPreviousRegReq = affiliatesWithTags ? [...affiliatesWithTags] : [];

  const { data: devices } = useDevices(collapse, (data) =>
    data.map((device) => device.name).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: operatingSystems } = useOperatingSystems(collapse, (data) =>
    data.map((operatingSystem) => operatingSystem.name).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: browsers } = useBrowsers(collapse, (data) =>
    data.map((browser) => browser.name).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: aibLists } = useAibList(collapse, (data) =>
    data.map((aibList) => aibList.listCode).sort((a, b) => (a > b ? 1 : -1))
  );

  return (
    <TabPane tabId='Users'>
      {source !== 'publisherPrePingCriteria' && (
        <ToggleSwitchRow label='TCPA Opt-in Required' name={generateName('criteriaOfferRequiresOptIn')} />
      )}
      <ToggleSwitchRow
        label='TCPA Opt-in Required Over Last Interval'
        name={generateName('criteriaTcpaLastOptInInterval')}
      />
      <ToggleSwitchRow
        label='Brite Verify Phone Validated'
        name={generateName('criteriaPhoneHasBeenVerifiedWithBriteverify')}
      />
      <ToggleSwitchRow
        label='Brite Verify Email Validated'
        name={generateName('criteriaEmailHasBeenVerifiedWithBriteverify')}
      />
      <ToggleSwitchRow label='Include users in the phone BlackList' name={generateName('criteriaPhoneBlacklist')} />
      <ToggleSwitchRow
        label='English Browser Language Required'
        name={generateName('englishBrowserLanguageRequired')}
      />
      <Grid container spacing={2} sx={{ alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <NumberField name={generateName('criteriaMinAge')} label='Min Age' />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <NumberField name={generateName('criteriaMaxAge')} label='Max Age' />
        </Grid>
      </Grid>
      {source !== 'publisherPrePingCriteria' && (
        <SelectRow
          name={generateName('criteriaExcludeBrowserType')}
          label='Exclude Browser Type'
          options={[
            { value: '', option: 'Select' },
            { value: 'Desktop', option: 'Desktop' },
            { value: 'Mobile', option: 'Mobile' },
          ]}
        />
      )}
      <SelectRow
        name={generateName('criteriaExcludeGender')}
        label='Exclude Gender'
        options={[
          { value: '', option: 'Select' },
          { value: 'female', option: 'Female' },
          { value: 'male', option: 'Male' },
        ]}
      />
      {source !== 'publisherPrePingCriteria' && (
        <SelectRow
          name={generateName('criteriaAnuraScore')}
          label='Exclude Anura Score'
          options={[
            { value: '', option: 'Select' },
            { value: 'NONSUSPECT', option: 'Non-suspect' },
            { value: 'SUSPECT', option: 'Suspect' },
            { value: 'NOSCORE', option: 'No Score' },
          ]}
        />
      )}
      {source !== 'publisherPrePingCriteria' ? (
        <SelectRow label='Required User Data' name={generateName('criteriaUserDataIncludeExclude')}>
          <ColCondition
            when={generateName('criteriaUserDataIncludeExclude')}
            is={(val) => val === 'Include' || val === 'Exclude'}
          >
            <MultiselectField
              name={generateName('criteriaRequiredUserData')}
              data={[
                'email',
                'first_name',
                'last_name',
                'gender',
                'dob',
                'phone',
                'zip',
                'state',
                'city',
                'address_1',
                'ip_zip',
              ]}
              parse={(value) => value.join(',')}
              format={(value) => {
                if (!value) return [];
                if (typeof value !== 'string') return [value];
                return value.split(',');
              }}
            />
          </ColCondition>
        </SelectRow>
      ) : (
        <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
          <Grid size={{ xs: 12, lg: 2 }}>
            <Typography variant='h5'>Required User Data</Typography>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <MultiselectField
              data={['email', 'first_name', 'last_name', 'gender', 'dob', 'phone', 'zip', 'state', 'city', 'address_1']}
              name={generateName('criteriaRequiredUserData')}
              parse={(value) => value}
              format={(value) => (Array.isArray(value) ? value : [])}
            />
          </Grid>
        </Grid>
      )}
      <PreviousRegistration
        domainsForPreviousRegReq={source === 'publisherPrePingCriteria' ? allDomains : domainsForPreviousRegReq}
        affiliatesForPreviousRegReq={affiliatesForPreviousRegReq}
        source={source}
        isLoading={domainsLoading || domainTagsLoading || affiliatesLoading || affiliateTagsLoading}
      />
      {source !== 'publisherPrePingCriteria' && (
        <>
          <SelectRow name={generateName('criteriaNullSourceNewUserIncludeExclude')} label='Null Source New Users' />
          <SelectRow name={generateName('criteriaAibListIncludeExclude')} label='AIB List'>
            <ColCondition when={generateName('criteriaAibListIncludeExclude')}>
              <MultiselectField data={aibLists} name={generateName('criteriaAibList')} />
            </ColCondition>
          </SelectRow>
        </>
      )}
      <SelectRow label='User_ID last digit' name={generateName('criteriaIncludeOrExcludeUserIdLastDigit')}>
        <ColCondition when={generateName('criteriaIncludeOrExcludeUserIdLastDigit')}>
          <MultiselectField
            data={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
            name={generateName('criteriaUserIdLastDigitString')}
            allowCreate={true}
          />
        </ColCondition>
      </SelectRow>
      {source !== 'publisherPrePingCriteria' && (
        <>
          <SelectRow label='Device' name={generateName('criteriaIncludeOrExcludeDevices')}>
            <ColCondition when={generateName('criteriaIncludeOrExcludeDevices')}>
              <MultiselectField data={devices} name={generateName('criteriaDevices')} />
            </ColCondition>
          </SelectRow>
          <SelectRow label='Browsers' name={generateName('criteriaIncludeOrExcludeBrowsers')}>
            <ColCondition when={generateName('criteriaIncludeOrExcludeBrowsers')}>
              <MultiselectField data={browsers} name={generateName('criteriaBrowsers')} />
            </ColCondition>
          </SelectRow>
          <SelectRow label='What If Domain' name={generateName('criteriaIncludeOrExcludeDomains')}>
            <ColCondition when={generateName('criteriaIncludeOrExcludeDomains')}>
              <MultiselectField
                data={domainsWithTags}
                name={generateName('criteriaDomains')}
                textField={(item) =>
                  item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`
                }
                dataKey='name'
                busy={domainsLoading || domainTagsLoading}
                groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Domains</span>)}
              />
            </ColCondition>
          </SelectRow>
          <SelectRow label='Operating System' name={generateName('criteriaIncludeOrExcludeOperatingSystems')}>
            <ColCondition when={generateName('criteriaIncludeOrExcludeOperatingSystems')}>
              <MultiselectField data={operatingSystems} name={generateName('criteriaOperatingSystems')} />
            </ColCondition>
          </SelectRow>
        </>
      )}
      <SelectRow label='States' name={generateName('criteriaIncludeOrExcludeStates')}>
        <ColCondition when={generateName('criteriaIncludeOrExcludeStates')}>
          <MultiselectField data={states} name={generateName('criteriaStates')} />
        </ColCondition>
      </SelectRow>
      <SelectRow label='Zip Code' name={generateName('criteriaIncludeOrExcludeZips')}>
        <ColCondition when={generateName('criteriaIncludeOrExcludeZips')}>
          <Grid container spacing={2} sx={{ alignItems: 'start' }}>
            <Grid size='grow'>
              <Field name={generateName('criteriaZips')} component={textarea} parse={normalizeZips} />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Field name={generateName('criteriaZips')}>
                {({ input }) => {
                  const zips = input.value?.split(/\s+/).filter((zip) => zip.trim().length === 5 && !isNaN(zip)) || [];
                  return (
                    <>
                      Count: {zips.length}
                      <br />
                      Unique: {new Set(zips).size}
                    </>
                  );
                }}
              </Field>
            </Grid>
          </Grid>
        </ColCondition>
      </SelectRow>
    </TabPane>
  );
};

export default UsersPane;
